import { getApiUrl, API_ENDPOINTS } from "../config/api.config";

/**
 * Fetch user funds and margins from the API
 * @returns {Promise<Object>} Funds data including equity, commodity margins
 */
export const fetchFunds = async () => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.FUNDS), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Send cookies with request
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Session expired. Please login again.");
      }
      throw new Error(`Failed to fetch funds: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching funds:", error);
    throw error;
  }
};

/**
 * Format funds data for display
 * @param {Object} fundsData - Raw funds data from API
 * @returns {Object} Formatted funds data
 */
export const formatFundsData = (fundsData) => {
  if (!fundsData || !fundsData.equity) {
    return {
      equity: {
        enabled: false,
        net: 0,
        available: 0,
        utilized: 0,
        opening_balance: 0,
      },
    };
  }

  const equity = fundsData.equity;

  const collateral = equity.available?.collateral || 0;
  const usedDebits = equity.used?.debits || 0;
  const remaining = collateral - usedDebits;

  return {
    equity: {
      enabled: equity.enabled || false,
      net: equity.net || 0,
      available: equity.available?.livebalance || equity.available?.cash || 0,
      opening_balance: equity.available?.openingbalance || 0,
      cash: equity.available?.cash || 0,
      collateral: collateral,
      utilized: usedDebits,
      used_debits: usedDebits,
      remaining: remaining,
      span_margin: equity.utilised?.span || 0,
      exposure_margin: equity.utilised?.exposure || 0,
    },
  };
};

/**
 * Format currency value for display
 * @param {number} value - Numeric value
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "₹0";
  }

  const numValue = Number(value);

  // For values over 1 lakh, show in lakhs
  if (Math.abs(numValue) >= 100000) {
    return `₹${(numValue / 100000).toFixed(2)}L`;
  }

  // For values over 1000, show with comma separation
  if (Math.abs(numValue) >= 1000) {
    return `₹${numValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  }

  // For smaller values, show with decimals
  return `₹${numValue.toFixed(2)}`;
};

/**
 * Get margin utilization percentage
 * @param {number} utilized - Utilized amount
 * @param {number} available - Available amount
 * @returns {number} Utilization percentage
 */
export const getMarginUtilization = (utilized, available) => {
  const total = utilized + available;
  if (total === 0) return 0;
  return Math.round((utilized / total) * 100);
};

/**
 * Fetch charges summary from the API
 * @returns {Promise<Object>} Charges summary data
 */
export const fetchChargesSummary = async () => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.CHARGES_SUMMARY), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Session expired. Please login again.");
      }
      throw new Error(`Failed to fetch charges summary: ${response.status}`);
    }

    const result = await response.json();
    return result.data || {};
  } catch (error) {
    console.error("Error fetching charges summary:", error);
    throw error;
  }
};

/**
 * Refresh orders from Zerodha and update charges summary
 * @returns {Promise<Object>} Updated charges summary and sync info
 */
export const refreshOrders = async () => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ORDERS_REFRESH), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Session expired. Please login again.");
      }
      throw new Error(`Failed to refresh orders: ${response.status}`);
    }

    const result = await response.json();
    return result.data || {};
  } catch (error) {
    console.error("Error refreshing orders:", error);
    throw error;
  }
};
